import { AuthHandoffRedeemRequestSchema, SessionResponseSchema } from '@pingo/shared';
import { api, buildUrl } from './api';
import { getSession, setSession } from './auth-store';
import { deviceId } from './device';
import { queryClient } from './query';

function revokeReplaced(token: string): void {
  try {
    void fetch(buildUrl('/api/v1/auth/logout'), {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: '{}',
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}

/**
 * Sign-in handed over by the mobile app (CLAUDE.md §15 #398): `…#handoff=<one-time code>` is
 * removed from the address bar at once (history, screenshots) and exchanged for this browser's own
 * USER session, replacing any session that was here (the person just came from their app). An
 * expired or used code is ignored: the page then asks for a login as usual.
 */
export async function consumeHandoff(loc: Location = window.location): Promise<boolean> {
  const params = new URLSearchParams(loc.hash.replace(/^#/, ''));
  const code = params.get('handoff');
  if (!code) return false;
  params.delete('handoff');
  const rest = params.toString();
  history.replaceState(history.state, '', `${loc.pathname}${loc.search}${rest ? `#${rest}` : ''}`);
  if (!AuthHandoffRedeemRequestSchema.safeParse({ code }).success) return false;
  try {
    const s = await api('/api/v1/auth/handoff/redeem', {
      method: 'POST',
      auth: 'none',
      body: { code, deviceId: deviceId() },
      schema: SessionResponseSchema,
    });
    const previous = getSession('USER');
    setSession(s);
    if (previous) {
      // a shared PC: never show the previous user's cached tenants / tournaments
      queryClient.clear();
      // every open of the app's in-app web screen hands over again (§15 #402): end the session it
      // replaces so repeated opens do not pile up live sessions (best effort, never blocks)
      if (previous.token !== s.token) revokeReplaced(previous.token);
    }
    return true;
  } catch {
    return false;
  }
}
